import { ContextData } from '../types/widget';
import { FormattedTemplate } from '../types/templates';
import { FeatureFlags } from '../types/featureFlags';
import { buildStructuredContext } from './buildStructuredContext';
import { extractStructuredContext } from './extractStructuredContext';
import { formatContextData } from './formatContextData';
import { detectPipelineVersion } from './templateUtils';

export interface ContextForPipeline {
  unstructured?: string;
  structured?: Record<string, unknown>;
}

/**
 * Build context for document generation based on pipeline version.
 *
 * V1 Pipeline:
 * - unstructured: All context items formatted as text via formatContextData()
 * - structured: Only from extractStructuredContext() if template is host-provided
 *
 * V2/V3 Pipeline:
 * - unstructured: Only patientDemographics and freeTextContext (global data for all sections)
 * - structured: All other context items via buildStructuredContext() for placeholder replacement
 */
export function buildContextForPipeline(
  contextData: ContextData,
  template: FormattedTemplate | undefined,
  featureFlags: FeatureFlags | undefined
): ContextForPipeline {
  const pipelineVersion = detectPipelineVersion(template?.template);

  let unstructured: string | undefined;
  let structured: Record<string, unknown> | undefined;

  if (pipelineVersion === 'v2' || pipelineVersion === 'v3') {
    const contextDataStructured = buildStructuredContext(contextData);

    const templateStructuredContext = extractStructuredContext(
      template,
      featureFlags
    );

    // Merge structured contexts: template context takes precedence
    structured =
      contextDataStructured || templateStructuredContext
        ? {
            ...contextDataStructured,
            ...templateStructuredContext,
          }
        : undefined;

    // Prefix patient demographics so the LLM can distinguish it from self-descriptive free-text notes.
    const unstructuredParts: string[] = [];

    const demographicsItem = contextData.find(
      (item) => item.key === 'patientDemographics'
    );
    if (demographicsItem?.value && typeof demographicsItem.value === 'string') {
      unstructuredParts.push(
        `Patient Demographics:\n${demographicsItem.value}`
      );
    }

    const freeTextItem = contextData.find(
      (item) => item.key === 'freeTextContext'
    );
    if (freeTextItem?.value && typeof freeTextItem.value === 'string') {
      unstructuredParts.push(freeTextItem.value);
    }

    unstructured =
      unstructuredParts.length > 0 ? unstructuredParts.join('\n\n') : undefined;
  } else {
    unstructured = formatContextData(contextData) || undefined;

    structured = extractStructuredContext(template, featureFlags);
  }

  return { unstructured, structured };
}
